/**
 * Line-based Business Card Detection
 * Uses Hough transform and line intersection for robust card detection
 */

export class LineBasedCardDetector {
  constructor() {
    this.debugCanvas = null;
    this.debugMode = false;
  }

  /**
   * Main detection function
   */
  async detectCard(image) {
    console.log('Starting line-based card detection');
    
    // Create working canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Step 1: Preprocess image
    const processed = this.preprocessImage(imageData);
    
    // Step 2: Detect edges
    const edges = this.detectEdges(processed);
    
    // Step 3: Find lines using Hough transform
    const lines = this.houghTransform(edges);
    
    // Step 4: Filter and merge similar lines
    const filteredLines = this.filterLines(lines, canvas.width, canvas.height);
    
    // Step 5: Find best quadrilateral from line intersections
    const corners = this.findBestQuadrilateral(filteredLines, canvas.width, canvas.height);
    
    if (this.debugMode && this.debugCanvas) {
      this.drawDebugVisualization(this.debugCanvas, edges, filteredLines, corners);
    }
    
    return corners;
  }

  /**
   * Preprocess image for better edge detection
   */
  preprocessImage(imageData) {
    const { data, width, height } = imageData;
    const processed = new Uint8ClampedArray(width * height);
    
    // Convert to grayscale with enhanced contrast
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      processed[i / 4] = gray;
    }
    
    // Apply bilateral filter to reduce noise while preserving edges
    const filtered = this.bilateralFilter(processed, width, height);
    
    // Enhance contrast using CLAHE (Contrast Limited Adaptive Histogram Equalization)
    const enhanced = this.applyCLAHE(filtered, width, height);
    
    return { data: enhanced, width, height };
  }

  /**
   * Bilateral filter for edge-preserving smoothing
   */
  bilateralFilter(data, width, height, d = 5, sigmaColor = 75, sigmaSpace = 75) {
    const filtered = new Uint8ClampedArray(data.length);
    const radius = Math.floor(d / 2);
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let weightSum = 0;
        let valueSum = 0;
        const centerValue = data[y * width + x];
        
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const ny = y + dy;
            const nx = x + dx;
            
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              const neighborValue = data[ny * width + nx];
              
              // Spatial weight
              const spatialDist = Math.sqrt(dx * dx + dy * dy);
              const spatialWeight = Math.exp(-(spatialDist * spatialDist) / (2 * sigmaSpace * sigmaSpace));
              
              // Color weight
              const colorDist = Math.abs(neighborValue - centerValue);
              const colorWeight = Math.exp(-(colorDist * colorDist) / (2 * sigmaColor * sigmaColor));
              
              const weight = spatialWeight * colorWeight;
              weightSum += weight;
              valueSum += weight * neighborValue;
            }
          }
        }
        
        filtered[y * width + x] = valueSum / weightSum;
      }
    }
    
    return filtered;
  }

  /**
   * Apply CLAHE for contrast enhancement
   */
  applyCLAHE(data, width, height, clipLimit = 2.0, tileSize = 8) {
    const enhanced = new Uint8ClampedArray(data.length);
    
    // Simplified CLAHE - divide image into tiles and equalize each
    const tileWidth = Math.ceil(width / tileSize);
    const tileHeight = Math.ceil(height / tileSize);
    
    for (let ty = 0; ty < tileSize; ty++) {
      for (let tx = 0; tx < tileSize; tx++) {
        const x0 = tx * tileWidth;
        const y0 = ty * tileHeight;
        const x1 = Math.min((tx + 1) * tileWidth, width);
        const y1 = Math.min((ty + 1) * tileHeight, height);
        
        // Build histogram for this tile
        const hist = new Array(256).fill(0);
        let pixelCount = 0;
        
        for (let y = y0; y < y1; y++) {
          for (let x = x0; x < x1; x++) {
            hist[data[y * width + x]]++;
            pixelCount++;
          }
        }
        
        // Apply clipping
        const clipThreshold = clipLimit * pixelCount / 256;
        let excess = 0;
        
        for (let i = 0; i < 256; i++) {
          if (hist[i] > clipThreshold) {
            excess += hist[i] - clipThreshold;
            hist[i] = clipThreshold;
          }
        }
        
        // Redistribute excess
        const avgIncrease = excess / 256;
        for (let i = 0; i < 256; i++) {
          hist[i] += avgIncrease;
        }
        
        // Build cumulative distribution
        const cdf = new Array(256);
        cdf[0] = hist[0];
        for (let i = 1; i < 256; i++) {
          cdf[i] = cdf[i - 1] + hist[i];
        }
        
        // Apply transformation
        for (let y = y0; y < y1; y++) {
          for (let x = x0; x < x1; x++) {
            const idx = y * width + x;
            const value = data[idx];
            enhanced[idx] = Math.round(255 * cdf[value] / pixelCount);
          }
        }
      }
    }
    
    return enhanced;
  }

  /**
   * Detect edges using improved Canny edge detection
   */
  detectEdges(processed) {
    const { data, width, height } = processed;
    const edges = new Uint8ClampedArray(width * height);
    
    // Calculate gradients using Sobel operators
    const gradients = new Float32Array(width * height);
    const directions = new Float32Array(width * height);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        // Sobel X
        const gx = 
          -1 * data[(y - 1) * width + (x - 1)] +
          1 * data[(y - 1) * width + (x + 1)] +
          -2 * data[y * width + (x - 1)] +
          2 * data[y * width + (x + 1)] +
          -1 * data[(y + 1) * width + (x - 1)] +
          1 * data[(y + 1) * width + (x + 1)];
        
        // Sobel Y
        const gy = 
          -1 * data[(y - 1) * width + (x - 1)] +
          -2 * data[(y - 1) * width + x] +
          -1 * data[(y - 1) * width + (x + 1)] +
          1 * data[(y + 1) * width + (x - 1)] +
          2 * data[(y + 1) * width + x] +
          1 * data[(y + 1) * width + (x + 1)];
        
        const idx = y * width + x;
        gradients[idx] = Math.sqrt(gx * gx + gy * gy);
        directions[idx] = Math.atan2(gy, gx);
      }
    }
    
    // Non-maximum suppression
    const suppressed = new Float32Array(width * height);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const grad = gradients[idx];
        const angle = directions[idx];
        
        // Quantize angle to 4 directions
        const dir = Math.round(angle / (Math.PI / 4)) & 3;
        
        let n1, n2;
        switch (dir) {
          case 0: // Horizontal
            n1 = gradients[y * width + (x - 1)];
            n2 = gradients[y * width + (x + 1)];
            break;
          case 1: // Diagonal /
            n1 = gradients[(y - 1) * width + (x + 1)];
            n2 = gradients[(y + 1) * width + (x - 1)];
            break;
          case 2: // Vertical
            n1 = gradients[(y - 1) * width + x];
            n2 = gradients[(y + 1) * width + x];
            break;
          case 3: // Diagonal \
            n1 = gradients[(y - 1) * width + (x - 1)];
            n2 = gradients[(y + 1) * width + (x + 1)];
            break;
        }
        
        if (grad >= n1 && grad >= n2) {
          suppressed[idx] = grad;
        }
      }
    }
    
    // Dynamic threshold calculation using Otsu's method
    const sortedGradients = Array.from(suppressed).filter(g => g > 0).sort((a, b) => a - b);
    const highThreshold = sortedGradients[Math.floor(sortedGradients.length * 0.9)];
    const lowThreshold = highThreshold * 0.4;
    
    // Double threshold and edge tracking
    const strong = 255;
    const weak = 50;
    
    for (let i = 0; i < suppressed.length; i++) {
      if (suppressed[i] > highThreshold) {
        edges[i] = strong;
      } else if (suppressed[i] > lowThreshold) {
        edges[i] = weak;
      }
    }
    
    // Edge tracking by hysteresis
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (edges[idx] === weak) {
          // Check if connected to strong edge
          let hasStrongNeighbor = false;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (edges[(y + dy) * width + (x + dx)] === strong) {
                hasStrongNeighbor = true;
                break;
              }
            }
            if (hasStrongNeighbor) break;
          }
          edges[idx] = hasStrongNeighbor ? strong : 0;
        }
      }
    }
    
    return { data: edges, width, height };
  }

  /**
   * Hough transform for line detection
   */
  houghTransform(edges) {
    const { data, width, height } = edges;
    const lines = [];
    
    // Hough parameters
    const rhoResolution = 1;
    const thetaResolution = Math.PI / 180;
    const threshold = Math.min(width, height) * 0.25; // Increase threshold for stronger lines
    
    // Calculate maximum rho
    const maxRho = Math.sqrt(width * width + height * height);
    const rhoSize = Math.ceil(maxRho * 2 / rhoResolution);
    const thetaSize = Math.ceil(Math.PI / thetaResolution);
    
    // Accumulator array
    const accumulator = new Int32Array(rhoSize * thetaSize);
    
    // Vote in Hough space
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[y * width + x] > 0) {
          for (let thetaIdx = 0; thetaIdx < thetaSize; thetaIdx++) {
            const theta = thetaIdx * thetaResolution;
            const rho = x * Math.cos(theta) + y * Math.sin(theta);
            const rhoIdx = Math.round((rho + maxRho) / rhoResolution);
            accumulator[rhoIdx * thetaSize + thetaIdx]++;
          }
        }
      }
    }
    
    // Find peaks in accumulator with non-maximum suppression
    const peaks = [];
    const suppressionRadius = 10; // Suppress nearby peaks
    const usedIndices = new Set();
    
    // First pass: find all peaks above threshold
    const allPeaks = [];
    for (let rhoIdx = 1; rhoIdx < rhoSize - 1; rhoIdx++) {
      for (let thetaIdx = 1; thetaIdx < thetaSize - 1; thetaIdx++) {
        const idx = rhoIdx * thetaSize + thetaIdx;
        const value = accumulator[idx];
        
        if (value > threshold) {
          allPeaks.push({
            rhoIdx,
            thetaIdx,
            value,
            rho: (rhoIdx * rhoResolution) - maxRho,
            theta: thetaIdx * thetaResolution
          });
        }
      }
    }
    
    // Sort by strength
    allPeaks.sort((a, b) => b.value - a.value);
    
    // Apply non-maximum suppression
    for (const peak of allPeaks) {
      const key = `${peak.rhoIdx},${peak.thetaIdx}`;
      if (usedIndices.has(key)) continue;
      
      // Check if this is a local maximum
      let isMax = true;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dt = -2; dt <= 2; dt++) {
          if (dr === 0 && dt === 0) continue;
          const neighborIdx = (peak.rhoIdx + dr) * thetaSize + (peak.thetaIdx + dt);
          if (neighborIdx >= 0 && neighborIdx < accumulator.length && 
              accumulator[neighborIdx] > peak.value) {
            isMax = false;
            break;
          }
        }
        if (!isMax) break;
      }
      
      if (isMax) {
        peaks.push({ 
          rho: peak.rho, 
          theta: peak.theta, 
          strength: peak.value 
        });
        
        // Mark nearby points as used
        for (let dr = -suppressionRadius; dr <= suppressionRadius; dr++) {
          for (let dt = -suppressionRadius; dt <= suppressionRadius; dt++) {
            usedIndices.add(`${peak.rhoIdx + dr},${peak.thetaIdx + dt}`);
          }
        }
      }
    }
    
    // Take top lines
    const topPeaks = peaks.slice(0, 30);
    
    // Convert to line segments extending beyond image bounds
    for (const peak of topPeaks) {
      const { rho, theta } = peak;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      
      // Extend lines beyond image bounds for better intersection
      const extension = Math.max(width, height) * 0.1;
      let points = [];
      
      if (Math.abs(cos) > 0.001) {
        // Not vertical - calculate where line intersects top and bottom
        const y1 = -extension;
        const x1 = (rho - y1 * sin) / cos;
        points.push({ x: x1, y: y1 });
        
        const y2 = height + extension;
        const x2 = (rho - y2 * sin) / cos;
        points.push({ x: x2, y: y2 });
      }
      
      if (Math.abs(sin) > 0.001) {
        // Not horizontal - calculate where line intersects left and right
        const x3 = -extension;
        const y3 = (rho - x3 * cos) / sin;
        if (!points.some(p => Math.abs(p.x - x3) < 1 && Math.abs(p.y - y3) < 1)) {
          points.push({ x: x3, y: y3 });
        }
        
        const x4 = width + extension;
        const y4 = (rho - x4 * cos) / sin;
        if (!points.some(p => Math.abs(p.x - x4) < 1 && Math.abs(p.y - y4) < 1)) {
          points.push({ x: x4, y: y4 });
        }
      }
      
      // Sort points by distance to get proper endpoints
      if (points.length >= 2) {
        points.sort((a, b) => (a.x + a.y) - (b.x + b.y));
        lines.push({
          start: points[0],
          end: points[points.length - 1],
          rho,
          theta,
          strength: peak.strength
        });
      }
    }
    
    return lines;
  }

  /**
   * Filter and merge similar lines
   */
  filterLines(lines, width, height) {
    if (lines.length === 0) return [];
    
    // Merge similar lines
    const merged = [];
    const used = new Set();
    
    for (let i = 0; i < lines.length; i++) {
      if (used.has(i)) continue;
      
      const line1 = lines[i];
      let mergedLine = { ...line1 };
      let count = 1;
      
      for (let j = i + 1; j < lines.length; j++) {
        if (used.has(j)) continue;
        
        const line2 = lines[j];
        
        // Check if lines are similar (with tighter thresholds)
        const thetaDiff = Math.abs(line1.theta - line2.theta);
        const rhoDiff = Math.abs(line1.rho - line2.rho);
        
        if ((thetaDiff < Math.PI / 72 || thetaDiff > Math.PI - Math.PI / 72) && rhoDiff < 10) {
          // Merge lines
          mergedLine.rho = (mergedLine.rho * count + line2.rho) / (count + 1);
          mergedLine.theta = (mergedLine.theta * count + line2.theta) / (count + 1);
          mergedLine.strength += line2.strength;
          count++;
          used.add(j);
        }
      }
      
      merged.push(mergedLine);
    }
    
    // Classify lines as horizontal or vertical
    const horizontal = [];
    const vertical = [];
    
    for (const line of merged) {
      const angle = line.theta * 180 / Math.PI;
      
      // More lenient angle thresholds for classification
      if ((angle < 45 || angle > 135)) {
        vertical.push(line);
      } else if (angle > 45 && angle < 135) {
        horizontal.push(line);
      }
    }
    
    // Sort by strength (keeping strongest lines)
    horizontal.sort((a, b) => b.strength - a.strength);
    vertical.sort((a, b) => b.strength - a.strength);
    
    // Take top lines but ensure we get both edges
    const selectedHorizontal = [];
    const selectedVertical = [];
    
    // For horizontal lines, try to get top and bottom edges
    if (horizontal.length > 0) {
      // Sort by rho to find extremes
      const hSorted = [...horizontal].sort((a, b) => a.rho - b.rho);
      
      // Add topmost and bottommost strong lines
      if (hSorted.length >= 2) {
        selectedHorizontal.push(hSorted[0]); // Top edge
        selectedHorizontal.push(hSorted[hSorted.length - 1]); // Bottom edge
        
        // Add more strong lines if available
        for (const line of horizontal) {
          if (!selectedHorizontal.includes(line) && selectedHorizontal.length < 4) {
            selectedHorizontal.push(line);
          }
        }
      } else {
        selectedHorizontal.push(...hSorted);
      }
    }
    
    // For vertical lines, try to get left and right edges
    if (vertical.length > 0) {
      // Sort by rho to find extremes
      const vSorted = [...vertical].sort((a, b) => a.rho - b.rho);
      
      // Add leftmost and rightmost strong lines
      if (vSorted.length >= 2) {
        selectedVertical.push(vSorted[0]); // Left edge
        selectedVertical.push(vSorted[vSorted.length - 1]); // Right edge
        
        // Add more strong lines if available
        for (const line of vertical) {
          if (!selectedVertical.includes(line) && selectedVertical.length < 4) {
            selectedVertical.push(line);
          }
        }
      } else {
        selectedVertical.push(...vSorted);
      }
    }
    
    console.log(`Selected ${selectedHorizontal.length} horizontal and ${selectedVertical.length} vertical lines`);
    return [...selectedHorizontal, ...selectedVertical];
  }

  /**
   * Find best quadrilateral from line intersections
   */
  findBestQuadrilateral(lines, width, height) {
    // Separate horizontal and vertical lines
    const horizontal = [];
    const vertical = [];
    
    for (const line of lines) {
      const angle = line.theta * 180 / Math.PI;
      if ((angle < 45 || angle > 135)) {
        vertical.push(line);
      } else if (angle > 45 && angle < 135) {
        horizontal.push(line);
      }
    }
    
    if (horizontal.length < 2 || vertical.length < 2) {
      console.log('Not enough lines for quadrilateral');
      return this.getFallbackCorners(width, height);
    }
    
    // Sort lines by position to get outermost ones
    horizontal.sort((a, b) => Math.abs(a.rho) - Math.abs(b.rho));
    vertical.sort((a, b) => Math.abs(a.rho) - Math.abs(b.rho));
    
    // Get the outermost lines (first and last after sorting by rho)
    // Sort by actual position (rho value)
    const hSortedByPos = [...horizontal].sort((a, b) => a.rho - b.rho);
    const vSortedByPos = [...vertical].sort((a, b) => a.rho - b.rho);
    
    const topLine = hSortedByPos[0];
    const bottomLine = hSortedByPos[hSortedByPos.length - 1];
    const leftLine = vSortedByPos[0];
    const rightLine = vSortedByPos[vSortedByPos.length - 1];
    
    console.log('Selected lines - Top rho:', topLine.rho, 'Bottom rho:', bottomLine.rho, 
                'Left rho:', leftLine.rho, 'Right rho:', rightLine.rho);
    
    // Find intersections of the 4 outermost lines
    const corners = [];
    
    // Top-left corner
    const topLeft = this.lineIntersection(topLine, leftLine);
    if (topLeft) corners.push(this.refineCorner(topLeft, edges, width, height, 'top-left'));
    
    // Top-right corner
    const topRight = this.lineIntersection(topLine, rightLine);
    if (topRight) corners.push(this.refineCorner(topRight, edges, width, height, 'top-right'));
    
    // Bottom-right corner
    const bottomRight = this.lineIntersection(bottomLine, rightLine);
    if (bottomRight) corners.push(this.refineCorner(bottomRight, edges, width, height, 'bottom-right'));
    
    // Bottom-left corner
    const bottomLeft = this.lineIntersection(bottomLine, leftLine);
    if (bottomLeft) corners.push(this.refineCorner(bottomLeft, edges, width, height, 'bottom-left'));
    
    // Validate corners
    if (corners.length === 4) {
      // Check if corners form a reasonable quadrilateral
      const bounds = this.getBounds(corners);
      const aspectRatio = bounds.width / bounds.height;
      
      // Business cards typically have aspect ratio between 1.4 and 2.0 (or inverse for portrait)
      if ((aspectRatio > 1.3 && aspectRatio < 2.2) || (aspectRatio > 0.45 && aspectRatio < 0.77)) {
        console.log('Found valid quadrilateral from outermost lines');
        return this.orderCorners(corners);
      }
    }
    
    // If outermost lines don't work, try all combinations
    const intersections = [];
    
    for (const hLine of horizontal) {
      for (const vLine of vertical) {
        const point = this.lineIntersection(hLine, vLine);
        // Allow points slightly outside image bounds since we're extending lines
        if (point && point.x >= -50 && point.x < width + 50 && point.y >= -50 && point.y < height + 50) {
          // Clamp to image bounds
          point.x = Math.max(0, Math.min(width - 1, point.x));
          point.y = Math.max(0, Math.min(height - 1, point.y));
          intersections.push(point);
        }
      }
    }
    
    if (intersections.length < 4) {
      console.log('Not enough intersections for quadrilateral');
      return this.getFallbackCorners(width, height);
    }
    
    // Find the best quadrilateral
    let bestQuad = null;
    let bestScore = 0;
    
    for (let i = 0; i < intersections.length - 3; i++) {
      for (let j = i + 1; j < intersections.length - 2; j++) {
        for (let k = j + 1; k < intersections.length - 1; k++) {
          for (let l = k + 1; l < intersections.length; l++) {
            const quad = [intersections[i], intersections[j], intersections[k], intersections[l]];
            const score = this.scoreQuadrilateral(quad, width, height);
            
            if (score > bestScore) {
              bestScore = score;
              bestQuad = quad;
            }
          }
        }
      }
    }
    
    if (bestQuad) {
      // Order corners properly
      return this.orderCorners(bestQuad);
    }
    
    return this.getFallbackCorners(width, height);
  }

  /**
   * Calculate line intersection point
   */
  lineIntersection(line1, line2) {
    // Convert polar (rho, theta) to parametric form
    const cos1 = Math.cos(line1.theta);
    const sin1 = Math.sin(line1.theta);
    const cos2 = Math.cos(line2.theta);
    const sin2 = Math.sin(line2.theta);
    
    // Calculate determinant
    const det = cos1 * sin2 - sin1 * cos2;
    
    if (Math.abs(det) < 0.00001) {
      return null; // Lines are parallel or nearly parallel
    }
    
    // Calculate intersection point
    // Using the formula for line intersection in normal form
    const x = (line1.rho * sin2 - line2.rho * sin1) / det;
    const y = (line2.rho * cos1 - line1.rho * cos2) / det;
    
    return { x, y };
  }

  /**
   * Refine corner position using local edge information
   */
  refineCorner(corner, edges, width, height, position) {
    // Search in a small window around the intersection point
    const searchRadius = 20;
    const { data: edgeData } = edges;
    
    // Clamp initial corner to image bounds
    let x = Math.max(0, Math.min(width - 1, corner.x));
    let y = Math.max(0, Math.min(height - 1, corner.y));
    
    // Find the strongest corner response in the search area
    let bestX = x;
    let bestY = y;
    let bestScore = 0;
    
    const startX = Math.max(0, Math.floor(x - searchRadius));
    const endX = Math.min(width - 1, Math.floor(x + searchRadius));
    const startY = Math.max(0, Math.floor(y - searchRadius));
    const endY = Math.min(height - 1, Math.floor(y + searchRadius));
    
    for (let sy = startY; sy <= endY; sy++) {
      for (let sx = startX; sx <= endX; sx++) {
        // Calculate corner score using Harris corner detector principle
        let score = this.calculateCornerScore(edgeData, sx, sy, width, height);
        
        // Weight by distance from original point (prefer closer points)
        const dist = Math.sqrt((sx - x) * (sx - x) + (sy - y) * (sy - y));
        score = score * Math.exp(-dist / searchRadius);
        
        if (score > bestScore) {
          bestScore = score;
          bestX = sx;
          bestY = sy;
        }
      }
    }
    
    console.log(`Refined ${position} corner from (${x.toFixed(1)}, ${y.toFixed(1)}) to (${bestX}, ${bestY})`);
    return { x: bestX, y: bestY };
  }

  /**
   * Calculate corner score at a point
   */
  calculateCornerScore(edgeData, x, y, width, height) {
    if (x < 2 || x >= width - 2 || y < 2 || y >= height - 2) {
      return 0;
    }
    
    // Simple corner detector: look for edge pixels in perpendicular directions
    let horizontalEdge = 0;
    let verticalEdge = 0;
    
    // Check horizontal edges
    for (let dx = -2; dx <= 2; dx++) {
      if (edgeData[y * width + (x + dx)] > 0) {
        horizontalEdge++;
      }
    }
    
    // Check vertical edges  
    for (let dy = -2; dy <= 2; dy++) {
      if (edgeData[(y + dy) * width + x] > 0) {
        verticalEdge++;
      }
    }
    
    // Good corner has both horizontal and vertical edges
    return horizontalEdge * verticalEdge;
  }

  /**
   * Score a quadrilateral based on business card properties
   */
  scoreQuadrilateral(quad, width, height) {
    // Calculate area
    const area = this.polygonArea(quad);
    const imageArea = width * height;
    const areaRatio = area / imageArea;
    
    // Area should be reasonable
    if (areaRatio < 0.1 || areaRatio > 0.9) {
      return 0;
    }
    
    // Calculate aspect ratio
    const bounds = this.getBounds(quad);
    const aspectRatio = bounds.width / bounds.height;
    
    // Business card aspect ratio is typically 1.75 (3.5" x 2")
    const targetRatio = 1.75;
    const aspectScore = 1 - Math.min(1, Math.abs(aspectRatio - targetRatio) / targetRatio);
    
    // Check if it's roughly rectangular
    const angles = this.getAngles(quad);
    let angleScore = 0;
    for (const angle of angles) {
      angleScore += 1 - Math.min(1, Math.abs(angle - 90) / 90);
    }
    angleScore /= 4;
    
    // Combine scores
    return areaRatio * 0.3 + aspectScore * 0.4 + angleScore * 0.3;
  }

  /**
   * Calculate polygon area
   */
  polygonArea(points) {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area / 2);
  }

  /**
   * Get bounding box of points
   */
  getBounds(points) {
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Get angles of quadrilateral
   */
  getAngles(points) {
    const angles = [];
    for (let i = 0; i < 4; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % 4];
      const p3 = points[(i + 2) % 4];
      
      const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
      const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };
      
      const dot = v1.x * v2.x + v1.y * v2.y;
      const cross = v1.x * v2.y - v1.y * v2.x;
      
      const angle = Math.atan2(cross, dot) * 180 / Math.PI;
      angles.push(Math.abs(angle));
    }
    return angles;
  }

  /**
   * Order corners: top-left, top-right, bottom-right, bottom-left
   */
  orderCorners(points) {
    // Find center
    const center = {
      x: points.reduce((sum, p) => sum + p.x, 0) / 4,
      y: points.reduce((sum, p) => sum + p.y, 0) / 4
    };
    
    // Sort by angle from center
    const sorted = points.map(p => ({
      ...p,
      angle: Math.atan2(p.y - center.y, p.x - center.x)
    })).sort((a, b) => a.angle - b.angle);
    
    // Find top-left (minimum sum of x and y)
    let topLeftIdx = 0;
    let minSum = sorted[0].x + sorted[0].y;
    for (let i = 1; i < 4; i++) {
      const sum = sorted[i].x + sorted[i].y;
      if (sum < minSum) {
        minSum = sum;
        topLeftIdx = i;
      }
    }
    
    // Reorder starting from top-left
    const ordered = [];
    for (let i = 0; i < 4; i++) {
      ordered.push(sorted[(topLeftIdx + i) % 4]);
    }
    
    return ordered.map(p => ({ x: p.x, y: p.y }));
  }

  /**
   * Fallback corners for when detection fails
   */
  getFallbackCorners(width, height) {
    const margin = Math.min(width, height) * 0.15;
    const aspectRatio = 1.75;
    
    let cardWidth, cardHeight;
    if (width / height > aspectRatio) {
      cardHeight = height - (margin * 2);
      cardWidth = cardHeight * aspectRatio;
    } else {
      cardWidth = width - (margin * 2);
      cardHeight = cardWidth / aspectRatio;
    }
    
    const centerX = width / 2;
    const centerY = height / 2;
    
    return [
      { x: centerX - cardWidth / 2, y: centerY - cardHeight / 2 },
      { x: centerX + cardWidth / 2, y: centerY - cardHeight / 2 },
      { x: centerX + cardWidth / 2, y: centerY + cardHeight / 2 },
      { x: centerX - cardWidth / 2, y: centerY + cardHeight / 2 }
    ];
  }

  /**
   * Draw debug visualization
   */
  drawDebugVisualization(canvas, edges, lines, corners) {
    const ctx = canvas.getContext('2d');
    
    // Draw edge image
    const imageData = ctx.createImageData(edges.width, edges.height);
    for (let i = 0; i < edges.data.length; i++) {
      const value = edges.data[i];
      imageData.data[i * 4] = value;
      imageData.data[i * 4 + 1] = value;
      imageData.data[i * 4 + 2] = value;
      imageData.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    
    // Draw detected lines
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    for (const line of lines) {
      ctx.beginPath();
      if (line.start && line.end) {
        ctx.moveTo(line.start.x, line.start.y);
        ctx.lineTo(line.end.x, line.end.y);
      }
      ctx.stroke();
    }
    
    // Draw corners
    if (corners && corners.length === 4) {
      ctx.strokeStyle = 'lime';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < 4; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      
      // Draw corner points
      ctx.fillStyle = 'yellow';
      for (const corner of corners) {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /**
   * Enable debug mode
   */
  setDebugMode(enabled, canvas = null) {
    this.debugMode = enabled;
    this.debugCanvas = canvas;
  }
}

// Export singleton instance
export const lineDetector = new LineBasedCardDetector();